import React, { useEffect, useState, useRef } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow, Circle, Polyline } from '@react-google-maps/api';
import { io, Socket } from 'socket.io-client';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://urbannerve.org';

interface Incident {
  id: string;
  lat: number;
  lng: number;
  type: string;
  priority: number;
  status: string;
  patientCount: number;
  assignedAmbulance?: string;
  assignedHospital?: string;
  flooded?: boolean;
  floodSeverity?: number;
}

interface Ambulance {
  id: string;
  unitNumber: string;
  lat: number;
  lng: number;
  status: string;
  assignedIncident?: string | null;
  eta?: number | null;
}

interface Hospital {
  id: string;
  name: string;
  lat: number;
  lng: number;
  totalBeds: number;
  availableBeds: number;
  currentPatients: number;
  specialties: string[];
  website?: string;
  phone?: string;
  address?: string;
  rating?: number;
}

interface FloodZone {
  lat: number;
  lng: number;
  radius: number;
  severity: number;
}

interface EmergencyInfo {
  country: string;
  emergency: string;
  ambulance: string;
}

interface SOSRequest {
  requestId: string;
  customerId: number;
  customerName: string;
  lat: number;
  lng: number;
  type: string;
  timestamp: string;
  hospitalName?: string;
  hospitalLat?: number;
  hospitalLng?: number;
  escalated?: boolean;
  escalationCount?: number;
}

interface NavStep {
  instruction: string;
  distance: string;
  maneuver: string;
}

const mapContainerStyle = { height: '100%', width: '100%' };
const mapCenter = { lat: 24.4539, lng: 54.3773 };

const makeIcon = (emoji: string, color: string) => ({
  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36">
      <circle cx="18" cy="18" r="16" fill="${color}" stroke="white" stroke-width="2"/>
      <text x="18" y="23" text-anchor="middle" font-size="16">${emoji}</text>
    </svg>
  `)}`
});

const makeSosIcon = () => ({
  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48">
      <circle cx="24" cy="24" r="22" fill="#ef4444" stroke="white" stroke-width="3" opacity="0.9"/>
      <circle cx="24" cy="24" r="18" fill="#ef4444" stroke="#fca5a5" stroke-width="2"/>
      <text x="24" y="30" text-anchor="middle" font-size="20">🚨</text>
    </svg>
  `)}`,
  scaledSize: { width: 48, height: 48 } as any,
  anchor: { x: 24, y: 24 } as any
});

const getDistanceKm = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  return (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))).toFixed(1);
};

const decodePolyline = (encoded: string) => {
  const points: {lat: number, lng: number}[] = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let shift = 0, result = 0, b;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
};

export const RealTimeMap: React.FC<{ userRole: string, token: string | null, userId: number, userName: string }> = ({ userRole, token, userId, userName }) => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [ambulances, setAmbulances] = useState<Ambulance[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [floodZones, setFloodZones] = useState<FloodZone[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [sosStatus, setSosStatus] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  const [emergencyInfo, setEmergencyInfo] = useState<EmergencyInfo>({ country: 'Unknown', emergency: '112', ambulance: '112' });
  const [sosRequests, setSosRequests] = useState<SOSRequest[]>([]);
  const [acceptedRequest, setAcceptedRequest] = useState<any>(null);
  const [routePath, setRoutePath] = useState<{lat: number, lng: number}[]>([]);
  const [showHospitalPicker, setShowHospitalPicker] = useState(false);
  const [etaAmbulance, setEtaAmbulance] = useState<string | null>(null);
  const [etaHospital, setEtaHospital] = useState<string | null>(null);
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [navSteps, setNavSteps] = useState<NavStep[]>([]);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [hospitalRoutePath, setHospitalRoutePath] = useState<{lat: number, lng: number}[]>([]);
  const [lastUpdateTime, setLastUpdateTime] = useState<string | null>(null);
  const [lastLocationSent, setLastLocationSent] = useState<string | null>(null);
  const userLocationRef = useRef<{lat: number, lng: number} | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const pollIntervalRef = useRef<any>(null);
  const trackingIntervalRef = useRef<any>(null);
  const ambulanceRouteIntervalRef = useRef<any>(null);
  const targetLocRef = useRef<{lat: number, lng: number} | null>(null);
  const jobStageRef = useRef<string>('accepted');          // ambulance: current stage
  const acceptedHospitalRef = useRef<{lat: number, lng: number} | null>(null); // ambulance: hospital coords
  const currentStageRef = useRef<string>('pending');       // customer: stage synced from poll
  const acceptedStatusRef = useRef<any>(null);             // customer: full accepted status

  useEffect(() => {
    const socket = io(BACKEND_URL, { transports: ['polling'] });
    socketRef.current = socket;

    socket.on('initial_data', (data) => {
      setIncidents(data.incidents);
      setAmbulances(data.ambulances);
      setFloodZones(data.floodZones);
    });

    fetch(`${BACKEND_URL}/api/incidents`).then(r => r.json()).then(setIncidents);
    fetch(`${BACKEND_URL}/api/ambulances`).then(r => r.json()).then(setAmbulances);
    fetch(`${BACKEND_URL}/api/floodzones`).then(r => r.json()).then(setFloodZones);

    socket.on('ambulances_updated', (data) => setAmbulances(data));
    socket.on('sos_received', (data) => {
      setIncidents(prev => [...prev, data.incident]);
      setAmbulances(prev => prev.map(a => a.id === data.ambulance.id ? data.ambulance : a));
    });
    socket.on('sos_escalated', (data) => {
      setSosRequests(prev => prev.map(r =>
        r.requestId === data.requestId ? { ...r, escalated: true, escalationCount: data.escalationCount } : r
      ));
    });

    // watchPosition gives a continuous GPS stream — fires on every position change
    let watchId: number | null = null;
    let firstFix = true;

    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(
        async (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          userLocationRef.current = loc;
          setUserLocation(loc);

          if (firstFix) {
            firstFix = false;
            fetch(`${BACKEND_URL}/api/nearby-hospitals?lat=${loc.lat}&lng=${loc.lng}`)
              .then(r => r.json()).then(setHospitals);
            fetch(`${BACKEND_URL}/api/emergency-info?lat=${loc.lat}&lng=${loc.lng}`)
              .then(r => r.json()).then(setEmergencyInfo);
            fetch(`${BACKEND_URL}/api/ambulances`).then(r => r.json()).then(setAmbulances);
          }

          if (token) {
            try {
              await fetch(`${BACKEND_URL}/api/auth/location`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, lat: loc.lat, lng: loc.lng })
              });
              setLastLocationSent(new Date().toLocaleTimeString());
            } catch {}
          }
        },
        (err) => {
          console.log('GPS unavailable:', err.message);
          setUserLocation(mapCenter);
          userLocationRef.current = mapCenter;
          fetch(`${BACKEND_URL}/api/hospitals`).then(r => r.json()).then(setHospitals);
          fetch(`${BACKEND_URL}/api/emergency-info?lat=${mapCenter.lat}&lng=${mapCenter.lng}`)
            .then(r => r.json()).then(setEmergencyInfo);
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
      );
    }

    // Ambulance polls for SOS requests every 2 seconds
    let ambulancePollInterval: any = null;
    if (userRole === 'ambulance') {
      ambulancePollInterval = setInterval(async () => {
        const res = await fetch(`${BACKEND_URL}/api/sos-requests`);
        const requests = await res.json();
        setSosRequests(requests);
      }, 2000);
    }

    return () => {
      socket.close();
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (ambulancePollInterval) clearInterval(ambulancePollInterval);
    };
  }, []);

  const STAGE_LABELS: Record<string, string> = {
    pending: '🔍 Finding ambulance...',
    accepted: '🚑 Ambulance en route',
    ambulance_arrived: '📍 Ambulance arrived!',
    en_route_hospital: '🏥 En route to hospital',
    completed: '✅ Arrived at hospital',
  };

  const handleSOS = async (hospital: Hospital | null) => {
    setShowHospitalPicker(false);
    const location = userLocationRef.current || mapCenter;
    setSosStatus(STAGE_LABELS.pending);
    try {
      const body: any = { token, lat: location.lat, lng: location.lng, type: 'medical_emergency' };
      if (hospital) {
        body.hospitalId = hospital.id;
        body.hospitalName = hospital.name;
        body.hospitalLat = hospital.lat;
        body.hospitalLng = hospital.lng;
      }
      const res = await fetch(`${BACKEND_URL}/api/sos-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        setActiveRequestId(data.requestId);
        let trackingStarted = false;

        pollIntervalRef.current = setInterval(async () => {
          const statusRes = await fetch(`${BACKEND_URL}/api/sos-status?requestId=${data.requestId}`);
          const status = await statusRes.json();

          if (status.status === 'not_found') {
            clearInterval(pollIntervalRef.current);
            return;
          }

          if (STAGE_LABELS[status.status]) setSosStatus(STAGE_LABELS[status.status]);
          // Keep stage ref in sync so the tracking interval can react to it
          currentStageRef.current = status.status;

          if (status.status === 'accepted' && !trackingStarted) {
            trackingStarted = true;
            acceptedStatusRef.current = status;
            setAcceptedRequest(status);
            if (status.etaAmbulance) setEtaAmbulance(status.etaAmbulance);
            if (status.etaHospital) setEtaHospital(status.etaHospital);

            // Draw green route: customer → hospital (one-time static preview)
            const customerLoc = userLocationRef.current;
            if (customerLoc && status.hospitalLat && status.hospitalLng) {
              fetch(`${BACKEND_URL}/api/route?originLat=${customerLoc.lat}&originLng=${customerLoc.lng}&destLat=${status.hospitalLat}&destLng=${status.hospitalLng}`)
                .then(r => r.json())
                .then(rd => { if (rd.polyline) setHospitalRoutePath(decodePolyline(rd.polyline)); });
            }

            // Stage-aware tracking interval: switches route destination as stage changes
            trackingIntervalRef.current = setInterval(async () => {
              const locRes = await fetch(`${BACKEND_URL}/api/ambulance-location?requestId=${data.requestId}`);
              const locData = await locRes.json();
              const currentLocation = userLocationRef.current;
              if (!locData || !locData.lat || !currentLocation) return;

              // Update ambulance marker position
              setAmbulances(prev => {
                const exists = prev.find(a => a.id === 'active_ambulance');
                if (exists) return prev.map(a => a.id === 'active_ambulance' ? { ...a, lat: locData.lat, lng: locData.lng } : a);
                return [...prev, { id: 'active_ambulance', unitNumber: locData.ambulanceName, lat: locData.lat, lng: locData.lng, status: 'en_route', assignedIncident: null, eta: null }];
              });

              const toHospital = currentStageRef.current === 'en_route_hospital';
              const accepted = acceptedStatusRef.current;
              const destLat = toHospital ? accepted?.hospitalLat : currentLocation.lat;
              const destLng = toHospital ? accepted?.hospitalLng : currentLocation.lng;
              if (!destLat || !destLng) return;

              // Redraw blue route every 2s — ambulance→customer or ambulance→hospital
              const routeRes = await fetch(`${BACKEND_URL}/api/route?originLat=${locData.lat}&originLng=${locData.lng}&destLat=${destLat}&destLng=${destLng}`);
              const routeData = await routeRes.json();
              if (routeData.polyline) setRoutePath(decodePolyline(routeData.polyline));
              if (routeData.duration) {
                if (toHospital) setEtaHospital(routeData.duration);
                else setEtaAmbulance(routeData.duration);
              }
              setLastUpdateTime(new Date().toLocaleTimeString());
            }, 2000);
            setTimeout(() => clearInterval(trackingIntervalRef.current), 3600000);
          }

          if (status.status === 'completed') {
            clearInterval(pollIntervalRef.current);
            clearInterval(trackingIntervalRef.current);
            setActiveRequestId(null);
          }
        }, 2000);
      }
    } catch {
      setSosStatus('❌ Connection failed');
    }
  };

  const handleCancel = async () => {
    if (!activeRequestId) return;
    try {
      await fetch(`${BACKEND_URL}/api/sos-cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, requestId: activeRequestId })
      });
      clearInterval(pollIntervalRef.current);
      setSosStatus(null);
      setActiveRequestId(null);
      setAcceptedRequest(null);
      setEtaAmbulance(null);
      setEtaHospital(null);
      setRoutePath([]);
      setHospitalRoutePath([]);
      setLastUpdateTime(null);
    } catch {
      console.error('Cancel failed');
    }
  };

  const advanceStage = async (stage: string) => {
    if (!activeRequestId) return;
    try {
      await fetch(`${BACKEND_URL}/api/sos-stage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, requestId: activeRequestId, stage })
      });
      jobStageRef.current = stage; // interval reads this on next tick — no re-render needed
      if (stage === 'completed') {
        clearInterval(ambulanceRouteIntervalRef.current);
        targetLocRef.current = null;
        setActiveRequestId(null);
        setNavSteps([]);
        setRoutePath([]);
        setHospitalRoutePath([]);
      }
    } catch {
      console.error('Stage update failed');
    }
  };

  const handleAccept = async (request: SOSRequest) => {
    try {
      await fetch(`${BACKEND_URL}/api/sos-accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, requestId: request.requestId })
      });
      setSosRequests(prev => prev.filter(r => r.requestId !== request.requestId));
      setActiveRequestId(request.requestId);

      // Store customer location and hospital coords for the interval
      targetLocRef.current = { lat: request.lat, lng: request.lng };
      jobStageRef.current = 'accepted';
      if (request.hospitalLat && request.hospitalLng) {
        acceptedHospitalRef.current = { lat: request.hospitalLat, lng: request.hospitalLng };
      }

      // Draw green route once: customer → hospital (static preview, both ends fixed)
      if (request.hospitalLat && request.hospitalLng) {
        const hospRes = await fetch(`${BACKEND_URL}/api/route?originLat=${request.lat}&originLng=${request.lng}&destLat=${request.hospitalLat}&destLng=${request.hospitalLng}`);
        const hospData = await hospRes.json();
        if (hospData.polyline) setHospitalRoutePath(decodePolyline(hospData.polyline));
      }

      // Stage-aware route draw — reads jobStageRef on every tick so it reacts to stage changes
      const drawRoute = async () => {
        const ambLoc = userLocationRef.current;
        if (!ambLoc) return;
        const toHospital = jobStageRef.current === 'en_route_hospital';
        const destLoc = toHospital ? acceptedHospitalRef.current : targetLocRef.current;
        if (!destLoc) return;
        const routeRes = await fetch(`${BACKEND_URL}/api/route?originLat=${ambLoc.lat}&originLng=${ambLoc.lng}&destLat=${destLoc.lat}&destLng=${destLoc.lng}`);
        const routeData = await routeRes.json();
        if (routeData.polyline) setRoutePath(decodePolyline(routeData.polyline));
        if (routeData.steps?.length > 0) setNavSteps(routeData.steps);
        if (routeData.duration) {
          if (toHospital) setEtaHospital(routeData.duration);
          else setEtaAmbulance(routeData.duration);
        }
        setLastLocationSent(new Date().toLocaleTimeString());
      };

      // Draw immediately, then every 2s
      drawRoute();
      ambulanceRouteIntervalRef.current = setInterval(drawRoute, 2000);
    } catch {
      console.error('Accept failed');
    }
  };

  const getFloodColor = (severity: number) => {
    if (severity >= 8) return '#ef4444';
    if (severity >= 5) return '#f97316';
    return '#eab308';
  };

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={userLocation || mapCenter}
          zoom={12}
          options={{ styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }] }}
        >
          {floodZones.map((zone, i) => (
            <Circle key={i} center={{ lat: zone.lat, lng: zone.lng }}
              radius={zone.radius * 111000}
              options={{ fillColor: getFloodColor(zone.severity), fillOpacity: 0.35, strokeColor: getFloodColor(zone.severity), strokeOpacity: 0.8, strokeWeight: 2 }}
            />
          ))}

          {routePath.length > 0 && (
            <Polyline path={routePath} options={{ strokeColor: '#2563eb', strokeWeight: 4, strokeOpacity: 0.8 }} />
          )}
          {hospitalRoutePath.length > 0 && (
            <Polyline path={hospitalRoutePath} options={{ strokeColor: '#16a34a', strokeWeight: 4, strokeOpacity: 0.8 }} />
          )}

          {incidents.map((incident) => (
            <Marker key={incident.id} position={{ lat: incident.lat, lng: incident.lng }}
              icon={makeIcon('🚨', incident.priority >= 80 ? '#ef4444' : '#f97316')}
              onClick={() => setSelected({ type: 'incident', data: incident })}
            />
          ))}

          {ambulances.map((amb) => (
            <Marker key={amb.id} position={{ lat: amb.lat, lng: amb.lng }}
              icon={makeIcon('🚑', amb.status === 'available' ? '#22c55e' : '#3b82f6')}
              onClick={() => setSelected({ type: 'ambulance', data: amb })}
            />
          ))}

          {hospitals.map((hosp) => (
            <Marker key={hosp.id} position={{ lat: hosp.lat, lng: hosp.lng }}
              icon={makeIcon('🏥', '#3b82f6')}
              onClick={() => setSelected({ type: 'hospital', data: hosp })}
            />
          ))}

          {userLocation && <Marker position={userLocation} icon={makeIcon('📍', '#8b5cf6')} />}

          {/* SOS location markers — visible to ambulance for all pending requests */}
          {userRole === 'ambulance' && sosRequests.map((req) => (
            <Marker
              key={`sos-${req.requestId}`}
              position={{ lat: req.lat, lng: req.lng }}
              icon={makeSosIcon()}
              onClick={() => setSelected({ type: 'sos', data: req })}
            />
          ))}

          {/* Pin for the accepted customer location (both roles) */}
          {activeRequestId && targetLocRef.current && (
            <Marker
              position={targetLocRef.current}
              icon={makeSosIcon()}
            />
          )}

          {selected && (
            <InfoWindow position={{ lat: selected.data.lat, lng: selected.data.lng }} onCloseClick={() => setSelected(null)}>
              <div style={{ padding: '8px', minWidth: '200px' }}>
                {selected.type === 'sos' && (
                  <>
                    <h3 style={{ fontWeight: 'bold', margin: '0 0 8px 0', color: '#ef4444' }}>🚨 SOS Request</h3>
                    <p style={{ margin: '0 0 4px 0' }}>👤 {selected.data.customerName}</p>
                    <p style={{ margin: '0 0 4px 0', fontSize: '0.75rem', color: '#666' }}>Type: {selected.data.type}</p>
                    {selected.data.hospitalName && <p style={{ margin: '0 0 4px 0', fontSize: '0.75rem', color: '#2563eb' }}>🏥 {selected.data.hospitalName}</p>}
                    {userLocation && <p style={{ margin: 0, fontSize: '0.75rem' }}>📏 {getDistanceKm(userLocation.lat, userLocation.lng, selected.data.lat, selected.data.lng)} km away</p>}
                  </>
                )}
                {selected.type === 'incident' && (
                  <>
                    <h3 style={{ fontWeight: 'bold', margin: '0 0 8px 0' }}>🚨 Incident #{selected.data.id}</h3>
                    <p>Type: {selected.data.type}</p>
                    <p>Priority: <strong>{selected.data.priority}</strong></p>
                    <p>Status: {selected.data.status}</p>
                    {selected.data.assignedAmbulance && <p>Ambulance: {selected.data.assignedAmbulance}</p>}
                    {selected.data.assignedHospital && <p>Hospital: {selected.data.assignedHospital}</p>}
                    {selected.data.flooded && <p style={{color:'red'}}>⚠️ Flood zone! Severity: {selected.data.floodSeverity}</p>}
                  </>
                )}
                {selected.type === 'ambulance' && (
                  <>
                    <h3 style={{ fontWeight: 'bold', margin: '0 0 8px 0' }}>🚑 {selected.data.unitNumber}</h3>
                    <p>Status: {selected.data.status.replace('_', ' ').toUpperCase()}</p>
                    {selected.data.eta && <p>ETA: {selected.data.eta} min</p>}
                  </>
                )}
                {selected.type === 'hospital' && (
                  <>
                    <h3 style={{ fontWeight: 'bold', margin: '0 0 8px 0' }}>🏥 {selected.data.name}</h3>
                    {selected.data.address && <p style={{fontSize:'0.75rem', color:'#666'}}>{selected.data.address}</p>}
                    {userLocation && <p>📏 Distance: <strong>{getDistanceKm(userLocation.lat, userLocation.lng, selected.data.lat, selected.data.lng)} km</strong></p>}
                    <p>Beds: {selected.data.availableBeds}/{selected.data.totalBeds}</p>
                    <p>Patients: {selected.data.currentPatients}</p>
                    {selected.data.rating && <p>⭐ Rating: {selected.data.rating}</p>}
                    {selected.data.phone && <p>📞 <a href={`tel:${selected.data.phone}`}>{selected.data.phone}</a></p>}
                    {selected.data.website && <p>🌐 <a href={selected.data.website} target="_blank" rel="noopener noreferrer">Visit Website</a></p>}
                  </>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>

      {/* Live Stats Panel */}
      <div style={{ position: 'absolute', top: '1rem', right: '1rem', backgroundColor: 'white', padding: '1rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 1000, maxWidth: '220px' }}>
        <h4 style={{ fontWeight: 'bold', margin: '0 0 8px 0' }}>📊 Live Stats</h4>
        <div style={{ fontSize: '0.875rem' }}>
          <div>🚨 Active Incidents: <strong>{incidents.filter(i => i.status === 'active').length}</strong></div>
          <div>🚑 Available Units: <strong>{ambulances.filter(a => a.status === 'available').length}</strong></div>
          <div>🏥 Nearby Hospitals: <strong>{hospitals.length}</strong></div>
          {hospitals.length > 0 && (
            <div style={{marginTop:'4px', fontSize:'0.75rem', maxHeight:'120px', overflowY:'auto'}}>
              {hospitals.map((h, i) => (
                <div key={i} style={{padding:'2px 0', borderBottom:'1px solid #f0f0f0'}}>
                  🏥 {h.name}
                  {userLocation && <span style={{color:'#666'}}> — {getDistanceKm(userLocation.lat, userLocation.lng, h.lat, h.lng)}km</span>}
                </div>
              ))}
            </div>
          )}
          <div>🌊 Flood Zones: <strong>{floodZones.length}</strong></div>
          <div style={{marginTop:'8px', paddingTop:'8px', borderTop:'1px solid #eee'}}>
            <div>📍 {emergencyInfo.country}</div>
            <div>🚨 Emergency: <strong>{emergencyInfo.emergency}</strong></div>
            <div>🚑 Ambulance: <strong>{emergencyInfo.ambulance}</strong></div>
          </div>
        </div>
      </div>

      {/* Ambulance - Incoming SOS Requests */}
      {userRole === 'ambulance' && !activeRequestId && sosRequests.length > 0 && (
        <div style={{ position: 'absolute', top: '1rem', left: '1rem', backgroundColor: 'white', padding: '1rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 1000, maxWidth: '280px' }}>
          <h4 style={{ fontWeight: 'bold', margin: '0 0 8px 0', color: '#ef4444' }}>🆘 Incoming SOS Requests</h4>
          {sosRequests.map((req) => (
            <div key={req.requestId} style={{ padding: '0.75rem', border: `1px solid ${req.escalated ? '#ef4444' : '#fca5a5'}`, borderRadius: '0.5rem', marginBottom: '0.5rem', backgroundColor: req.escalated ? '#fff1f1' : '#fef2f2' }}>
              {req.escalated && <p style={{ margin: '0 0 4px 0', fontSize: '0.75rem', fontWeight: 'bold', color: '#ef4444' }}>⚠️ No response — {(req.escalationCount || 0) * 30}s elapsed</p>}
              <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>👤 {req.customerName}</p>
              <p style={{ margin: '0 0 4px 0', fontSize: '0.75rem', color: '#666' }}>Type: {req.type}</p>
              {userLocation && <p style={{ margin: '0 0 4px 0', fontSize: '0.75rem', color: '#666' }}>📏 {getDistanceKm(userLocation.lat, userLocation.lng, req.lat, req.lng)} km away</p>}
              {req.hospitalName && (
                <p style={{ margin: '0 0 8px 0', fontSize: '0.75rem', color: '#2563eb' }}>
                  🏥 {req.hospitalName}
                  {req.hospitalLat != null && ` — ${getDistanceKm(req.lat, req.lng, req.hospitalLat, req.hospitalLng!)} km from customer`}
                </p>
              )}
              <button onClick={() => handleAccept(req)} style={{ width: '100%', padding: '0.5rem', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontWeight: 'bold' }}>
                ✅ Accept
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Ambulance - Navigation Panel (shown after accepting) */}
      {userRole === 'ambulance' && activeRequestId && (
        <div style={{ position: 'absolute', top: '1rem', left: '1rem', backgroundColor: 'white', padding: '1rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 1000, maxWidth: '300px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h4 style={{ fontWeight: 'bold', margin: 0, color: '#2563eb' }}>🗺️ Navigation</h4>
            {lastLocationSent
              ? <span style={{ fontSize: '0.65rem', color: '#16a34a' }}>📡 {lastLocationSent}</span>
              : <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>📡 sharing...</span>
            }
          </div>
          {navSteps.length > 0 ? (
            <>
              <div style={{ backgroundColor: '#eff6ff', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '0.5rem' }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '0.875rem' }}>{navSteps[currentStepIdx]?.instruction}</p>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>{navSteps[currentStepIdx]?.distance}</p>
              </div>
              <p style={{ margin: '0 0 8px 0', fontSize: '0.75rem', color: '#6b7280', textAlign: 'center' }}>Step {currentStepIdx + 1} of {navSteps.length}</p>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <button onClick={() => setCurrentStepIdx(i => Math.max(0, i - 1))} disabled={currentStepIdx === 0} style={{ flex: 1, padding: '0.4rem', backgroundColor: '#e5e7eb', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}>← Prev</button>
                <button onClick={() => setCurrentStepIdx(i => Math.min(navSteps.length - 1, i + 1))} disabled={currentStepIdx === navSteps.length - 1} style={{ flex: 1, padding: '0.4rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}>Next →</button>
              </div>
            </>
          ) : (
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 8px 0' }}>Route drawn on map</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <button onClick={() => advanceStage('ambulance_arrived')} style={{ padding: '0.4rem', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.875rem' }}>📍 Mark Arrived at Customer</button>
            <button onClick={() => advanceStage('en_route_hospital')} style={{ padding: '0.4rem', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.875rem' }}>🏥 Heading to Hospital</button>
            <button onClick={() => advanceStage('completed')} style={{ padding: '0.4rem', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.875rem' }}>✅ Complete Job</button>
          </div>
        </div>
      )}

      {/* Customer - Hospital Picker */}
      {userRole === 'customer' && showHospitalPicker && (
        <div style={{ position: 'absolute', bottom: '11rem', left: '50%', transform: 'translateX(-50%)', zIndex: 1001, backgroundColor: 'white', borderRadius: '0.75rem', padding: '1rem', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', width: '320px' }}>
          <h4 style={{ margin: '0 0 4px 0', color: '#1f2937' }}>🏥 Choose a Hospital</h4>
          <p style={{ margin: '0 0 10px 0', fontSize: '0.75rem', color: '#6b7280' }}>Select your preferred hospital</p>
          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {hospitals.map((hosp) => (
              <div
                key={hosp.id}
                onClick={() => handleSOS(hosp)}
                style={{ padding: '0.6rem 0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer', backgroundColor: '#f9fafb' }}
              >
                <div style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>{hosp.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>
                  {userLocation && `📏 ${getDistanceKm(userLocation.lat, userLocation.lng, hosp.lat, hosp.lng)} km away`}
                  {hosp.availableBeds != null && ` · 🛏 ${hosp.availableBeds} beds`}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => handleSOS(null)}
            style={{ width: '100%', padding: '0.5rem', marginTop: '0.5rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            Send without preference
          </button>
          <button
            onClick={() => setShowHospitalPicker(false)}
            style={{ width: '100%', padding: '0.5rem', marginTop: '0.5rem', backgroundColor: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Customer - SOS Button */}
      {userRole === 'customer' && (
        <div style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, textAlign: 'center' }}>
          {sosStatus && (
            <div style={{ backgroundColor: 'white', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', marginBottom: '1rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', fontSize: '0.875rem', maxWidth: '300px' }}>
              <div>{sosStatus}</div>
              {activeRequestId && sosStatus === STAGE_LABELS.pending && (
                <button onClick={handleCancel} style={{ marginTop: '0.5rem', width: '100%', padding: '0.35rem', backgroundColor: 'white', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.75rem' }}>
                  ❌ Cancel SOS
                </button>
              )}
            </div>
          )}
          {acceptedRequest && (
            <div style={{ backgroundColor: '#f0fdf4', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', border: '1px solid #86efac', fontSize: '0.875rem', maxWidth: '300px' }}>
              <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', color: '#16a34a' }}>🚑 {acceptedRequest.ambulanceName} accepted!</p>
              {etaAmbulance && <p style={{ margin: '0 0 4px 0', color: '#1f2937' }}>⏱ Arriving in: <strong>{etaAmbulance}</strong></p>}
              {acceptedRequest.hospitalName && <p style={{ margin: '0 0 4px 0', color: '#1f2937' }}>🏥 {acceptedRequest.hospitalName}</p>}
              {etaHospital && <p style={{ margin: '0 0 4px 0', color: '#1f2937' }}>🚗 To hospital: <strong>{etaHospital}</strong></p>}
              {lastUpdateTime
                ? <p style={{ margin: 0, fontSize: '0.7rem', color: '#16a34a' }}>🔴 Live · updated {lastUpdateTime}</p>
                : <p style={{ margin: 0, fontSize: '0.7rem', color: '#9ca3af' }}>⏳ Waiting for location...</p>
              }
            </div>
          )}
          <button
            onClick={() => setShowHospitalPicker(true)}
            disabled={!!sosStatus}
            style={{ backgroundColor: sosStatus ? '#9ca3af' : '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '80px', height: '80px', fontSize: '1rem', fontWeight: 'bold', cursor: sosStatus ? 'default' : 'pointer', boxShadow: sosStatus ? 'none' : '0 0 0 8px rgba(239,68,68,0.3)' }}
          >
            🆘 SOS
          </button>
        </div>
      )}
    </div>
  );
};